import React, { useState } from 'react';
import classnames from 'classnames';
import BaseCollapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/material';

interface CollapseProps {
  header: React.ReactNode;
  children: React.ReactNode;
  isExpanded?: boolean;
  toggle?: () => void;
  customWrapperClassName?: string;
  customHeaderWrapperClassName?: string;
  unmountOnExit?: boolean;
  sx?: SxProps;
}

const Collapse = ({
  header,
  children,
  isExpanded,
  toggle: baseToggle,
  customWrapperClassName = '',
  customHeaderWrapperClassName = '',
  unmountOnExit = false,
  sx,
}: CollapseProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isContentShown = isExpanded || isOpen;

  const toggle = (event: React.MouseEvent<HTMLElement>) => {
    (event.target as HTMLInputElement).blur();

    if (baseToggle) {
      baseToggle();
    } else {
      setIsOpen(!isOpen);
    }
  };

  return (
    <Box
      sx={[
        theme => ({
          '&:not(.collapse-wrapper--open):last-child > .collapse-header-wrapper': {
            borderBottom: '1px solid',
            borderBottomColor: 'border.default',
          },
          '& .collapse-title-wrapper': {
            transition: 'padding-top 0.2s linear',
            [theme.breakpoints.down('lg')]: { flexWrap: 'wrap' },
          },
          '& .collapse-title': {
            m: 0,
            color: 'text.primary',
            fontSize: '15px',
            fontWeight: 600,
            transition: 'font-size 0.2s linear, padding-top 0.2s linear',
            [theme.breakpoints.down('lg')]: { width: '100%' },
            '&.mr-auto': { mr: 'auto' },
          },
          '&.collapse-wrapper--open .collapse-title': {
            fontSize: '16px',
            [theme.breakpoints.down('lg')]: { pt: '0.75rem' },
          },
          '&.collapse-wrapper--open .collapse-title-wrapper': {
            [theme.breakpoints.up('lg')]: { pt: '0.75rem' },
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      className={classnames('collapse-wrapper', customWrapperClassName, { 'collapse-wrapper--open': isContentShown })}
    >
      <Box
        role="button"
        tabIndex={0}
        onKeyPress={() => {}}
        className={classnames('collapse-header-wrapper', customHeaderWrapperClassName, {
          'collapse-header-wrapper--open': isContentShown,
        })}
        sx={{
          position: 'relative',
          display: 'block',
          width: '100%',
          p: '13px 0 13px 15px',
          color: 'text.primary',
          textAlign: 'left',
          backgroundColor: 'background.paper',
          border: 0,
          borderTop: '1px solid',
          borderTopColor: 'border.default',
          borderRadius: 0,
          cursor: 'pointer',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: isContentShown ? '58%' : '49%',
            right: '13px',
            display: 'block',
            width: '10px',
            height: '10px',
            border: '2px solid',
            borderColor: 'text.primary',
            borderTop: 0,
            borderLeft: 0,
            transform: isContentShown ? 'translateY(-20%) rotate(225deg)' : 'translateY(-70%) rotate(45deg)',
            transformOrigin: 'center',
            transition: 'transform 0.3s',
          },
          '&:hover, &:focus, &:active': {
            color: 'text.primary',
            backgroundColor: 'background.paper',
            borderTopColor: 'border.default',
            outline: 'none',
            boxShadow: 'none !important',
          },
          '&:hover::after': {
            transform: isContentShown
              ? 'translateY(-20%) rotate(225deg) scale(1.25)'
              : 'translateY(-80%) rotate(45deg) scale(1.25)',
          },
        }}
        onClick={toggle}
      >
        {header}
      </Box>
      <BaseCollapse in={isContentShown} unmountOnExit={unmountOnExit}>
        {children}
      </BaseCollapse>
    </Box>
  );
};

export default Collapse;
